import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { IntroNote } from "@/components/IntroNote";
import { MfaEnroll } from "@/components/MfaEnroll";
import { SessionsPanel } from "@/components/SessionsPanel";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { ShieldCheck, ShieldOff, KeyRound, Download, Trash2 } from "lucide-react";
import { isSuperuser } from "@/lib/permissions";
import { Navbar } from "@/components/Navbar";

export default function Security() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const owner = isSuperuser(user);
  const [status, setStatus] = useState(null);
  const [disableCode, setDisableCode] = useState("");
  const [regenCode, setRegenCode] = useState("");
  const [newRecovery, setNewRecovery] = useState([]);
  const [enforce, setEnforce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [erasePassword, setErasePassword] = useState("");
  const [eraseOpen, setEraseOpen] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/auth/mfa/status");
      setStatus(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadStatus();
    if (owner) {
      api.get("/auth/mfa/enforcement").then(({ data }) => setEnforce(!!data.enforce_admins)).catch(() => {});
    }
  }, [owner]);

  const onEnrolled = async () => {
    await refreshUser();
    await loadStatus();
    toast.success("Two-factor authentication enabled.");
  };

  const disable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { code: disableCode.trim() });
      setDisableCode("");
      await refreshUser();
      await loadStatus();
      toast.success("Two-factor authentication disabled.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not disable MFA.");
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/recovery/regenerate", { code: regenCode.trim() });
      setNewRecovery(data.recovery_codes || []);
      setRegenCode("");
      await loadStatus();
      toast.success("New recovery codes generated.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not regenerate codes.");
    } finally {
      setBusy(false);
    }
  };

  const toggleEnforce = async (val) => {
    try {
      const { data } = await api.put("/auth/mfa/enforcement", { enforce_admins: val });
      setEnforce(!!data.enforce_admins);
      toast.success(val ? "MFA now required for Owner & admins." : "MFA enforcement turned off.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update policy.");
    }
  };

  const exportData = async () => {
    try {
      const res = await api.get("/gdpr/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "erio-data-export.json";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Your data export has been downloaded.");
    } catch {
      toast.error("Could not export your data.");
    }
  };

  const eraseAccount = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/gdpr/erase", { password: erasePassword });
      toast.success("Your account has been erased.");
      setEraseOpen(false);
      logout();
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not erase your account.");
    } finally {
      setBusy(false);
    }
  };

  const enabled = status?.enabled;

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Navbar />
      <main className="mx-auto max-w-2xl px-6 py-8" data-testid="security-page">
      <Seo title="Security" description="Manage two-factor authentication for your ERIO account." path="/security" />
      <p className="ek-eyebrow">Account</p>
      <h1 className="ek-heading mt-1 text-3xl">Security</h1>

      <IntroNote>
        Protect your account with two-factor authentication (2FA). When enabled, you'll enter a
        one-time code from your authenticator app each time you sign in.
      </IntroNote>

      {/* MFA status card */}
      <div className="rounded-xl border border-black/5 bg-white p-6" data-testid="mfa-card">
        <div className="flex items-center gap-3">
          <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${enabled ? "bg-green-100 text-green-700" : "bg-ek-purple/10 text-ek-purple"}`}>
            {enabled ? <ShieldCheck size={20} /> : <ShieldOff size={20} />}
          </span>
          <div>
            <p className="font-display text-base font-semibold text-ek-ink">
              Two-factor authentication
            </p>
            <p className="text-sm text-ek-ink/65" data-testid="mfa-state-label">
              {enabled ? `On · ${status?.recovery_remaining ?? 0} recovery codes remaining` : "Off"}
              {status?.enforced && !enabled ? " · required by your organisation" : ""}
            </p>
          </div>
        </div>

        <div className="mt-5">
          {!enabled ? (
            <MfaEnroll onDone={onEnrolled} />
          ) : (
            <div className="space-y-6">
              {/* Regenerate recovery codes */}
              <form onSubmit={regenerate} className="space-y-2" data-testid="mfa-regen-form">
                <p className="text-sm font-medium text-ek-ink">Regenerate recovery codes</p>
                <p className="text-xs text-ek-ink/65">Enter a current code to issue a fresh set (the old codes stop working).</p>
                <div className="flex gap-2">
                  <input
                    value={regenCode}
                    onChange={(e) => setRegenCode(e.target.value)}
                    required
                    data-testid="mfa-regen-code-input"
                    placeholder="Code or recovery code"
                    className="flex-1 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-ek-purple"
                  />
                  <button type="submit" disabled={busy} data-testid="mfa-regen-button" className="inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm font-medium hover:bg-ek-purple/5">
                    <KeyRound size={15} /> Regenerate
                  </button>
                </div>
                {newRecovery.length > 0 && (
                  <div className="mt-2 grid grid-cols-2 gap-2 rounded-lg border border-black/5 bg-ek-canvas p-3" data-testid="mfa-new-recovery">
                    {newRecovery.map((rc) => (
                      <span key={rc} className="rounded bg-white px-2 py-1 text-center font-mono text-xs">{rc}</span>
                    ))}
                  </div>
                )}
              </form>

              {/* Disable MFA */}
              <form onSubmit={disable} className="space-y-2 border-t border-black/5 pt-5" data-testid="mfa-disable-form">
                <p className="text-sm font-medium text-ek-ink">Turn off two-factor authentication</p>
                <div className="flex gap-2">
                  <input
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value)}
                    required
                    data-testid="mfa-disable-code-input"
                    placeholder="Current code or recovery code"
                    className="flex-1 rounded-lg border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-ek-purple"
                  />
                  <button type="submit" disabled={busy} data-testid="mfa-disable-button" className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50">
                    <ShieldOff size={15} /> Disable
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Data & privacy (GDPR) */}
      <div className="mt-6 rounded-xl border border-black/5 bg-white p-6" data-testid="gdpr-card">
        <p className="font-display text-base font-semibold text-ek-ink">Your data &amp; privacy</p>
        <p className="mt-0.5 text-sm text-ek-ink/65">
          Download a copy of the personal data we hold about you, or permanently erase your account.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button type="button" onClick={exportData} data-testid="gdpr-export-button" className="inline-flex items-center gap-2 rounded-lg border border-black/10 bg-white px-4 py-2 text-sm font-medium hover:bg-ek-purple/5">
            <Download size={15} /> Download my data
          </button>
          {!owner && (
            <Dialog open={eraseOpen} onOpenChange={(o) => { setEraseOpen(o); if (!o) setErasePassword(""); }}>
              <DialogTrigger asChild>
                <button type="button" data-testid="gdpr-erase-open" className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50">
                  <Trash2 size={15} /> Delete my account
                </button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md" data-testid="gdpr-erase-dialog">
                <DialogHeader>
                  <DialogTitle>Delete your account</DialogTitle>
                  <DialogDescription>
                    This permanently erases your account and anonymises your records. This cannot be undone.
                    Enter your password to confirm.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={eraseAccount} className="space-y-3">
                  <input
                    type="password"
                    value={erasePassword}
                    onChange={(e) => setErasePassword(e.target.value)}
                    required
                    data-testid="gdpr-erase-password"
                    placeholder="Your password"
                    className="w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-ek-purple"
                  />
                  <button type="submit" disabled={busy} data-testid="gdpr-erase-confirm" className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60">
                    <Trash2 size={15} /> {busy ? "Erasing…" : "Permanently delete my account"}
                  </button>
                </form>
              </DialogContent>
            </Dialog>
          )}
          <a href="/privacy" target="_blank" rel="noreferrer" data-testid="gdpr-privacy-link" className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-ek-purple hover:underline">
            Privacy &amp; data protection →
          </a>
        </div>
      </div>

      {/* Active sessions */}
      <SessionsPanel />

      {/* Owner-only enforcement */}
      {owner && (
        <div className="mt-6 rounded-xl border border-black/5 bg-white p-6" data-testid="mfa-enforcement-card">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-display text-base font-semibold text-ek-ink">Require MFA for Owner &amp; admins</p>
              <p className="mt-0.5 text-sm text-ek-ink/65">
                When on, the Owner and any admin must set up two-factor authentication before they can use the platform.
              </p>
            </div>
            <Switch checked={enforce} onCheckedChange={toggleEnforce} data-testid="mfa-enforce-toggle" />
          </div>
        </div>
      )}
      </main>
    </div>
  );
}
